import styled from '@emotion/styled';
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Autocomplete } from '@mui/material';
import SearchIcon from '@/../public/icons/SearchIcon.png';
import CloseIcon from '@/../public/icons/close.png';
import ReactCarousel from 'react-multi-carousel';
import { useExploreQuery } from '../hooks/use-explore-query';
import { useLocationFilterModalAtom } from '../hooks/use-location-filter-modal';
import { useLocations, getLocationsFromSanity } from '../hooks/use-locations';
import { StyledAvatar } from './dropdown-button';
import { AllLocationCard, LocationCard } from './location-card';
import React from 'react';
import useSetLocality from '../hooks/use-set-locality';
import { Global, css } from '@emotion/react';
import Image from 'next/image';
import { urlFor } from '@/utils/buildImageSource';

const StartIcon = () => (
  <StyledAvatar
    src={CloseIcon.src}
    style={{ borderRadius: 0, objectFit: 'contain', height: 24, width: 24 }}
  ></StyledAvatar>
);
const Back = styled(IconButton)`
  color: #000000;
  position: absolute;
  left: 0;
  :hover {
    color: #f8d057;
  }
  margin-left: 10px;
`;
const Forward = styled(IconButton)`
  color: #000000;
  position: absolute;
  right: 0;
  :hover {
    color: #f8d057;
  }
  margin-right: 10px;
`;
const StyledCard = styled(Card)`
  padding: 50px 10%;
  background-color: white;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  position: relative;
  @media (max-width: 768px) {
    padding: 20px 10px;
  }
`;
const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 1900 },
    items: 4,
  },
  desktop: {
    breakpoint: { max: 1900, min: 1200 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1200, min: 768 },
    items: 2,
    partialVisibilityGutter: 50,
  },
  mobile: {
    breakpoint: { max: 768, min: 0 },
    items: 1,
    partialVisibilityGutter: 50,
  },
};

interface LocationPosition {
  lat: number;
  lng: number;
}

interface Location {
  label: string;
  locality: string;
  administrativeAreaLevel1: string;
  position: LocationPosition;
  imgSrc: string;
}

const CustomRightArrow = ({ onClick }: { onClick?: () => void }) => {
  return (
    <Forward onClick={() => onClick?.()}>
      <ArrowForward style={{ fontSize: '3rem' }} />
    </Forward>
  );
};

const CustomLeftArrow = ({ onClick }: { onClick?: () => void }) => {
  return (
    <Back onClick={() => onClick?.()}>
      <ArrowBack style={{ fontSize: '3rem' }} />
    </Back>
  );
};

export function LocationFilterModal() {
  const [locations, setLocations] = React.useState<Location[]>([]);
  const { setLocationFilterModal } = useLocationFilterModalAtom();
  const { data } = useExploreQuery();
  const setLocality = useSetLocality();

  const handleClose = () => {
    setLocationFilterModal(false);
  };

  const fetchLocations = async () => {
    const locations: Location[] = await getLocationsFromSanity();
    setLocations(locations);
  };

  React.useEffect(() => {
    fetchLocations();
  }, []);

  const isMobile = useMediaQuery('(max-width:768px)');
  return (
    <StyledCard>
      <IconButton onClick={handleClose} style={{ position: 'absolute', top: 20, right: 20 }}>
        <StartIcon />
      </IconButton>
      <CardContent>
        <Grid container spacing={4}>
          <Grid item>
            <Typography
              style={{
                textAlign: 'center',
                fontFamily: 'var(--font-cooper-light)',
                fontSize: isMobile ? 25 : 30,
                fontStyle: 'normal',
                fontWeight: 400,
                marginRight: 10,
                marginTop: 35,
              }}
            >
              Show me experiences in:{' '}
            </Typography>
          </Grid>
          <Grid item>
            <form>
              <Autocomplete
                id="locality-autocomplete"
                options={locations}
                getOptionLabel={option => {
                  return `${option.locality}, ${option.administrativeAreaLevel1}`;
                }}
                onChange={(_, value) => {
                  if (value && typeof value !== 'string') {
                    setLocality(value.locality);
                    setLocationFilterModal(false);
                  }
                }}
                renderInput={params => (
                  <div>
                    <TextField
                      {...params}
                      InputLabelProps={{ shrink: false }}
                      variant="standard"
                      placeholder="Type a city"
                      style={
                        isMobile
                          ? {
                              borderRadius: 46,
                              border: '4px solid #ECECEC',
                              width: '300px',
                            }
                          : {
                              borderRadius: 46,
                              border: '4px solid #ECECEC',
                              width: 'max-content',
                            }
                      }
                      InputProps={{
                        style: {
                          height: 47,
                          width: 325,
                          paddingLeft: 15,
                        },
                        disableUnderline: true,
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position={isMobile ? 'start' : 'end'}>
                            <Image
                              src={SearchIcon.src}
                              alt=""
                              height={isMobile ? 16 : 20}
                              width={isMobile ? 16 : 20}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                )}
              />
            </form>
          </Grid>
          <Global
            styles={css`
              .react-multi-carousel-item {
                display: flex;
                flex-direction: row;
                justify-content: center;

                @media (max-width: 768px) {
                  justify-content: start;
                }
              }
              .react-multi-carousel-track {
                margin: 0 6px;
              }
              .react-multi-carousel-list {
                position: unset;
                padding-top: 13px;
                padding-bottom: 10px;
                a:hover {
                  text-decoration: none;
                }
              }
            `}
          />
          {locations.length > 0 && (
            <ReactCarousel
              additionalTransfrom={0}
              swipeable
              arrows
              centerMode={false}
              responsive={responsive}
              ssr
              partialVisible={isMobile}
              keyBoardControl={true}
              transitionDuration={500}
              slidesToSlide={isMobile ? 1 : 2}
              containerClass="container"
              removeArrowOnDeviceType={['mobile']}
              deviceType={isMobile ? 'mobile' : 'desktop'}
              sliderClass=""
              customRightArrow={<CustomRightArrow />}
              customLeftArrow={<CustomLeftArrow />}
            >
              <AllLocationCard {...{ numExp: data?.experiences?.totalCount || 0 }} />
              {locations.map((location, index) => (
                <LocationCard
                  key={index}
                  locality={location.locality}
                  administrativeLevel1={location.administrativeAreaLevel1}
                  imgSrc={urlFor(location.imgSrc)}
                />
              ))}
            </ReactCarousel>
          )}
        </Grid>
      </CardContent>
    </StyledCard>
  );
}
